import { PriceBook } from '@rta/rta-blueprint-library';
import { useEffect, useState } from 'react';

import { fetchPriceBookCatalog } from '../external-endpoints/fetch-price-book-catalog';
import { fetchPriceBooks, type PriceBookVersionSummary } from '../external-endpoints/fetch-price-books';
import { useKeystoneSession } from '../auth/use-keystone-session';

export type UsePriceBookParams = {
	/** The context every price read in the tree goes through; null before a design is open. */
	context: { applyPriceBook: (priceBook: PriceBook | null) => void } | null;
	/** The kitchen's stored pin. Null follows whichever version is current, matching the server resolver. */
	pinnedVersionId: string | null;
};

export type UsePriceBookResult = {
	appliedVersionId: string | null;
	appliedVersionName: string | null;
	versions: Array<PriceBookVersionSummary>;
	error: string | null;
};

// Immutable once published, so one fetch per version per session is all anyone needs.
const catalogCache = new Map<string, Promise<PriceBook | null>>();

/**
 * Prices the open design against a published snapshot instead of the live catalog. An unpinned design
 * follows the current version, which is exactly what `resolveDesignPriceBook` does on the server — the
 * two must agree or the estimate on screen and the estimate on the quote would differ.
 *
 * The catalog is swapped onto the existing context rather than rebuilding the Blueprint, so the
 * controller, the viewport and any unsaved edit survive the change.
 * @param params the blueprint context and the kitchen's stored pin
 * @returns which version is actually applied, the list to choose from, and any load failure
 */
export const usePriceBook = (params: UsePriceBookParams): UsePriceBookResult => {
	const { context, pinnedVersionId } = params;
	const { getToken } = useKeystoneSession();

	const [versions, setVersions] = useState<Array<PriceBookVersionSummary>>([]);
	const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
	const [appliedVersionId, setAppliedVersionId] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;

		const load = async (): Promise<void> => {
			const [result] = await fetchPriceBooks({ getToken });
			if (cancelled || !result) return;
			setVersions(result.versions);
			setCurrentVersionId(result.currentVersionId);
		};

		load();
		return () => { cancelled = true; };
	}, [getToken]);

	// The pointer, never the list order: an older book re-minted after a newer one (a restored
	// pre-increase baseline) publishes LAST, and the head of the list is then the wrong version.
	const effectiveVersionId = pinnedVersionId ?? currentVersionId;

	useEffect(() => {
		let cancelled = false;

		const apply = async (): Promise<void> => {
			if (!context) return;

			if (effectiveVersionId === null) {
				context.applyPriceBook(null);
				setAppliedVersionId(null);
				return;
			}

			const cached = catalogCache.get(effectiveVersionId) ?? (async (): Promise<PriceBook | null> => {
				const [catalog] = await fetchPriceBookCatalog({ versionId: effectiveVersionId, getToken });
				return catalog ? new PriceBook(catalog as never) : null;
			})();

			catalogCache.set(effectiveVersionId, cached);

			const priceBook = await cached.catch(() => null);

			// A failed load must not be remembered as a permanent miss.
			if (!priceBook) catalogCache.delete(effectiveVersionId);
			if (cancelled) return;

			if (!priceBook) {
				setError('Failed to load the price book catalog');
				return;
			}

			try {
				context.applyPriceBook(priceBook);
				setError(null);
				setAppliedVersionId(effectiveVersionId);
			} catch (applyError) {
				// An empty or malformed snapshot throws rather than silently repricing on the live catalog.
				setError((applyError as Error).message);
			}
		};

		apply();
		return () => { cancelled = true; };
	}, [context, effectiveVersionId, getToken]);

	return {
		appliedVersionId,
		appliedVersionName: versions.find(({ id }) => id === appliedVersionId)?.name ?? null,
		versions,
		error
	};
};
